<template>
  <v-row>
    <v-col>
      <v-card>
        <ValidationObserver ref="organizationForm" v-slot="{ invalid, validated }">
          <v-expansion-panels multiple>
            <v-expansion-panel v-test="{ panel: 'generalInfo' }">
              <ValidationObserver ref="info" v-slot="{ failed }">
                <v-expansion-panel-header>
                  <div id="info">
                    <v-icon>
                      info
                    </v-icon>
                    {{ $t('Общая информация') }}
                    <v-icon v-if="failed" color="error">
                      warning
                    </v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-row>
                    <v-col
                        cols="12"
                        md="6"
                        class="text-left">
                      <validatable-text-input
                          v-test="{ input: 'organizationShortName' }"
                          :value="organizationInfo.organizationShortName"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Короткое наименование')"
                          @change="changeProperty({ organizationShortName: $event })" />
                      <validatable-text-input
                          v-test="{ input: 'organizationFullName' }"
                          :value="organizationInfo.organizationFullName"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Полное наименование')"
                          @change="changeProperty({ organizationFullName: $event })" />
                      <validatable-radio-group
                          v-test="{ radio: 'participantType' }"
                          :value="organizationInfo.participantType"
                          :items="participantTypes"
                          :items-tips="PARTICIPANT_TYPE_TIP_ENUM"
                          readonly
                          rules="required"
                          :label="$t('Категория участия')"
                          @change="changeProperty({ participantType: $event })" />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-date-picker
                          :value="organizationInfo.registrationDate"
                          rules="required"
                          :readonly="readonly"
                          min-selected-date="1900-01-01"
                          :label="$t('Дата регистрации юридического лица')"
                          @change="changeProperty({ registrationDate: $event })" />
                      <validatable-identity-dictionary-selection
                          :value="organizationInfo.country"
                          rules="required"
                          :readonly="readonly"
                          :label="$t('Страна')"
                          prepend-icon="language"
                          :dictionary-type="DICTIONARY_TYPES.COUNTRIES"
                          @change="changeProperty({ country: $event })" />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3" />
                  </v-row>
                </v-expansion-panel-content>
              </ValidationObserver>
            </v-expansion-panel>

            <v-expansion-panel v-test="{ panel: 'eio' }">
              <ValidationObserver ref="eio" v-slot="{ failed }">
                <v-expansion-panel-header>
                  <div id="eio">
                    <v-icon>
                      person
                    </v-icon>
                    {{ $t('Единоличный исполнительный орган') }}
                    <v-icon v-if="failed" color="error">
                      warning
                    </v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-row>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'managerFIO' }"
                          :value="organizationInfo.managerFIO"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('ФИО')"
                          @change="changeProperty({ managerFIO: $event })" />
                    </v-col>
                    <v-col>
                      <validatable-text-input
                          v-test="{ input: 'managerPosition' }"
                          :value="organizationInfo.managerPosition"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Должность')"
                          @change="changeProperty({ managerPosition: $event })" />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </ValidationObserver>
            </v-expansion-panel>

            <v-expansion-panel v-test="{ panel: 'requsites' }">
              <ValidationObserver ref="requsites" v-slot="{ failed }">
                <v-expansion-panel-header>
                  <div id="requsites">
                    <v-icon>
                      business
                    </v-icon>
                    {{ $t('Реквизиты') }}
                    <v-icon
                        v-if="failed"
                        color="error">
                      warning
                    </v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-row>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'inn' }"
                          :value="organizationInfo.inn"
                          :readonly="readonly"
                          :rules="{ required: isRussianCountryCode || !organizationInfo.ogrn,
                                    max: 100,
                                    inn: isRussianCountryCode }"
                          :label="$t('ИНН')"
                          @change="changeProperty({ inn: $event })" />
                      <validatable-text-input
                          v-if="isRussianCountryCode"
                          v-test="{ input: 'kpp' }"
                          :value="organizationInfo.kpp"
                          :rules="{ required: isRussianCountryCode,
                                    max: 10,
                                    kpp: isRussianCountryCode }"
                          :readonly="readonly"
                          :label="$t('КПП')"
                          @change="changeProperty({ kpp: $event })" />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'ogrn' }"
                          :value="organizationInfo.ogrn"
                          :rules="{ required: isRussianCountryCode || !organizationInfo.inn,
                                    max: 100,
                                    ogrn: isRussianCountryCode }"
                          :readonly="readonly"
                          :label="$t('ОГРН/КИО')"
                          @change="changeProperty({ ogrn: $event })" />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </ValidationObserver>
            </v-expansion-panel>

            <v-expansion-panel v-test="{ panel: 'contacts' }">
              <ValidationObserver ref="contacts" v-slot="{ failed }">
                <v-expansion-panel-header>
                  <div id="contacts">
                    <v-icon>
                      contact_mail
                    </v-icon>
                    {{ $t('Контактная информация') }}
                    <v-icon
                        v-if="failed"
                        color="error">
                      warning
                    </v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-row>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <!-- <validatable-text-input
                          v-test="{ input: 'postAddress' }"
                          :value="organizationInfo.postAddress"
                          rules="required"
                          :readonly="readonly"
                          prepend-icon="local_post_office"
                          :label="$t('Почтовый адрес')"
                          @change="changeProperty({ postAddress: $event })" />
                      <validatable-text-input
                          v-test="{ input: 'realAddress' }"
                          :value="organizationInfo.realAddress"
                          rules="required"
                          :readonly="readonly"
                          prepend-icon="location_city"
                          :label="$t('Фактический адрес')"
                          @change="changeProperty({ realAddress: $event })" /> -->
                      <validatable-text-input
                          v-test="{ input: 'location' }"
                          :value="organizationInfo.location"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Место нахождения')"
                          prepend-icon="location_on"
                          @change="changeProperty({ location: $event })" />
                      <validatable-text-input
                          v-test="{ input: 'contactPerson' }"
                          :value="organizationInfo.contactPerson"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Контактное лицо')"
                          prepend-icon="person"
                          @change="changeProperty({ contactPerson: $event })" />

                      <validatable-text-input
                          v-test="{ input: 'contactPersonPosition' }"
                          :value="organizationInfo.contactPersonPosition"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Должность контактного лица')"
                          prepend-icon="person"
                          @change="changeProperty({ contactPersonPosition: $event })" />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'phone' }"
                          :value="organizationInfo.phone"
                          :readonly="readonly"
                          :rules="{ required: true,
                                    regex: /^[+]?[\d\s(\-);,]+$/ }"
                          :label="$t('Контактный телефон')"
                          prepend-icon="settings_phone"
                          :type="'tel'"
                          @change="changeProperty({ phone: $event })" />
                      <validatable-mail-list
                          v-test="{ 'mail-list': 'email' }"
                          :value="emailArray"
                          rules="required"
                          :label="$t('Адрес электронной почты')"
                          :readonly="readonly"
                          @change="changeEmail" />

                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </ValidationObserver>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-alert
              class="text-center"
              :value="invalid && validated"
              type="error">
            {{ $t('Не все поля заполнены.') }}
          </v-alert>
          <v-alert
              class="text-center"
              :value="!!countryError"
              type="error">
            {{ countryError }}
          </v-alert>
        </ValidationObserver>
      </v-card>
    </v-col>
  </v-row>

</template>

<script>
  import ValidatableRadioGroup from '@/components/reused/ui/ValidatableRadioGroup.vue'
  import ValidatableDatePicker from '@/components/reused/ui/ValidatableDatePicker.vue'
  import ValidatableMailList from '@/components/reused/ui/ValidatableMailList.vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableIdentityDictionarySelection from '@/components/reused/ui/ValidatableIdentityDictionarySelection.vue'

  import {
    PARTICIPANT_TYPE_TIP_ENUM
  } from '@/constants/enums'
  import {
    DICTIONARY_TYPES,
    RUSSIAN_COUNTRY_ID,
    PARTICIPANT_TYPES
  } from '@/constants/business'

  export default {
    components: {
      ValidatableRadioGroup,
      ValidatableDatePicker,
      ValidatableMailList,
      ValidatableTextInput,
      ValidatableIdentityDictionarySelection
    },
    props: {
      readonly: {
        type: Boolean,
        default: false
      },
      organizationInfo: {
        type: Object,
        required: true
      },
      participantTypes: {
        type: Object,
        required: true
      },
      readonlyParticipantTypes: {
        type: Boolean
      }
    },
    data () {
      return {
        isLoading: false,
        DICTIONARY_TYPES,
        PARTICIPANT_TYPE_TIP_ENUM
      }
    },
    computed: {
      isRussianCountryCode () {
        return this.organizationInfo.country === RUSSIAN_COUNTRY_ID
      },
      emailArray () {
        return this.organizationInfo?.email?.split(';') || []
      },
      countryError () {
        var isParticipant = this.organizationInfo.participantType === PARTICIPANT_TYPES.PARTICIPANT

        return isParticipant && this.isRussianCountryCode
          ? this.$t('Резидентам РФ на ЭПЭ услуга по регистрации для участия в процедурах реализации не предоставляется')
          : null
      }
    },
    methods: {
      changeProperty: function (objWithEditedProps) {
        if (objWithEditedProps?.country &&
        objWithEditedProps?.country !== RUSSIAN_COUNTRY_ID) {
          objWithEditedProps.kpp = null
          objWithEditedProps.ogrn = null
        }
        this.$emit('change', objWithEditedProps)
      },
      changeEmail: function (value) {
        const email = Array.isArray(value) ? value.join(';') : `${value}`
        this.changeProperty({ email })
      },
      validate: async function () {
        const result = await this.$refs.organizationForm.validate()
        return !this.countryError && result
      }
    }
  }
</script>
